import { useConfig } from '../context/ConfigContext';
import { formatMoney, roundMoney } from '../utils/currencyUtil';
import PosSelectedOptions from './PosSelectedOptions';
import '../css/Pos.css';

/**
 * One order line on the receipt, as table rows.
 *
 * A PLAIN LINE is one row, exactly as before: name, Qty., Unit Price, Amount.
 *
 * A LINE WITH PAID ADDITIONS is broken down so the figures can be checked in
 * the receipt's own columns:
 *
 *   Milktea Okinawa Powder      1    ₱80    ₱80
 *     Sugar Level: 50%
 *     Toppings: Coffee Jelly    1    ₱10    ₱10
 *     Extra: Pearl              2    ₱15    ₱30
 *
 * The product row carries the BASE price; every priced answer gets a row of
 * its own, with its count across the whole line (two extra pearls on each of
 * three drinks is 6), its price per piece and its amount. Free answers still
 * get a row — the barista needs them — but leave the money columns blank.
 * The amounts down the column add up to the line total.
 *
 * The breakdown needs the base price, which is stored on orders placed since
 * customization existed. Without it (an older order) the line is shown the
 * old way — answers listed under the name, one combined unit price — rather
 * than guessing a split that was never recorded.
 *
 * @param {object} props
 * @param {{name: string, quantity: number, price: number, lineTotal: number, basePrice?: number|null, options?: Array<object>|null}} props.item
 * @returns {JSX.Element}
 */
function PosReceiptItemRows({ item }) {
  const { config } = useConfig();
  const peso = (value) => formatMoney(value, config);

  const options = Array.isArray(item?.options) ? item.options.filter((option) => option?.label && option?.value) : [];
  const lineQty = Number(item?.quantity) || 0;
  const hasBase = item?.basePrice !== null && item?.basePrice !== undefined && Number.isFinite(Number(item.basePrice));
  const breakdown = hasBase && options.some((option) => Number(option.price) > 0);

  if (!breakdown) {
    return (
      <tr>
        <td className="pos-receipt-name">
          {item?.name}
          <PosSelectedOptions options={options} className="pos-receipt-opts" />
        </td>
        <td className="num">{item?.quantity}</td>
        <td className="num">{peso(item?.price)}</td>
        <td className="num">{peso(item?.lineTotal)}</td>
      </tr>
    );
  }

  const base = Number(item.basePrice);
  return (
    <>
      <tr className="pos-receipt-parent">
        <td className="pos-receipt-name">{item.name}</td>
        <td className="num">{lineQty}</td>
        <td className="num">{peso(base)}</td>
        <td className="num">{peso(roundMoney(base * lineQty))}</td>
      </tr>
      {options.map((option, index) => {
        const perItem = Number(option.price) || 0;
        // Pieces per item (an extra taken twice) times the line's quantity.
        const count = (Number(option.quantity) > 0 ? Number(option.quantity) : 1) * lineQty;
        const unit = option.unitPrice !== null && option.unitPrice !== undefined
          ? Number(option.unitPrice)
          : perItem;
        return (
          // Index in the key: several extras share the label "Extra".
          <tr key={`${option.label}-${index}`} className="pos-receipt-sub">
            <td className="pos-receipt-name">
              <span className="pos-selected-opt-label">{option.label}:</span> {option.name || option.value}
            </td>
            <td className="num">{perItem > 0 ? count : ''}</td>
            <td className="num">{perItem > 0 ? peso(unit) : ''}</td>
            <td className="num">{perItem > 0 ? peso(roundMoney(perItem * lineQty)) : ''}</td>
          </tr>
        );
      })}
    </>
  );
}

export default PosReceiptItemRows;
